import React, { useEffect, useState } from 'react';
import './Modal.css';

const ResultsModal = ({ isOpen, onClose, results, onCopy, theme, themeExplanation, date, isDarkMode, mistakes, answers, hints, updateFeedback, populationStats, stats, gameType = 'classic' }) => {
  const [buttonText, setButtonText] = useState('Share Your Results');
  const [countdown, setCountdown] = useState('');
  const [feedback, setFeedback] = useState(false);

  useEffect(() => {
    const updateCountdown = () => {
      const timeLeft = getTimeUntilNextDayUTC();
      setCountdown(formatTime(timeLeft));
    };

    updateCountdown();
    const intervalId = setInterval(updateCountdown, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setButtonText('Share Your Results');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleLike = async () => {
    if (!feedback && updateFeedback) {
      await updateFeedback(1, 0, date);
      setFeedback(true);
    }
  };
  const handleDislike = async () => {
    if (!feedback && updateFeedback) {
      await updateFeedback(0, 1, date);
      setFeedback(true);
    }
  };

  const handleCopy = () => {
    onCopy();
    setButtonText('Results Copied!');
  };

  const countCheckEmojis = (str) => {
    return (str?.split('✅').length - 1) || 0;
  };

  function getTimeUntilNextDayUTC() {
    const now = new Date();
    const utcNow = new Date(now.toISOString());
    const nextDayUTC = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), utcNow.getUTCDate() + 1));
    return nextDayUTC - utcNow;
  }

  function formatTime(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  // Determine user category for analytics display highlight
  const userCorrect = gameType === 'classic' ? countCheckEmojis(results) : answers;
  const userCategory = userCorrect === 3 || (gameType === 'connections' && userCorrect === 4) ? 'win' :
    userCorrect >= 2 ? 'great_attempt' :
      userCorrect === 1 ? 'good_attempt' : 'loss';

  const getPerformanceTitle = () => {
    if (userCategory === 'win' && mistakes === 0) return "Perfect!";
    if (userCategory === 'win') return "Nice Work!";
    if (userCorrect >= 1) return "Good Try!";
    return "Phew!";
  };

  return (
    <div className={`modal-overlay ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="modal-content results-nyt-style">
        <button className="close-button" onClick={onClose}>×</button>
        <div className="performance-header">
          <div style={{ fontSize: '32px', fontWeight: '800', marginBottom: '15px', letterSpacing: '-0.02em' }}>
            {gameType === 'connections' ? 'iconn🧩ct' : 'mojie classic'}
          </div>
          <h2 className="performance-title">{getPerformanceTitle()}</h2>
        </div>

        <div className="nyt-stats-row">
          <div className="nyt-stat-card">
            <span className="nyt-stat-value">{stats?.gamesPlayed || 0}</span>
            <span className="nyt-stat-label">Completed</span>
          </div>
          <div className="nyt-stat-card">
            <span className="nyt-stat-value">{Math.round(((stats?.gamesWon || 0) / (stats?.gamesPlayed || 1)) * 100)}%</span>
            <span className="nyt-stat-label">Win %</span>
          </div>
          <div className="nyt-stat-card">
            <span className="nyt-stat-value">{stats?.currentStreak || 0}</span>
            <span className="nyt-stat-label">Current Streak</span>
          </div>
          <div className="nyt-stat-card">
            <span className="nyt-stat-value">{stats?.maxStreak || 0}</span>
            <span className="nyt-stat-label">Max Streak</span>
          </div>
        </div>

        <div className="results-emoji-display" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
          {(() => {
            if (gameType === 'connections') {
              // Extract only the colored square rows
              const lines = results.split('\n');
              const emojiLines = lines.filter(line =>
                line.includes('🟨') || line.includes('🟩') ||
                line.includes('🟦') || line.includes('🟪') ||
                line.includes('⬜️')
              );
              return emojiLines.join('\n');
            }
            return results;
          })()}
        </div>

        <button className="share-results-button-nyt" onClick={handleCopy}>{buttonText}</button>

        {gameType === 'classic' && populationStats && (
          <div className="stats-comparison-nyt">
            <h2>How Hard Was Today?</h2>
            <p className="nyt-sample-text">Based on a sample of thousands of games</p>

            <div className="solve-rate-section">
              <span className="solve-rate-label">SOLVE RATE</span>
              <div className="solve-rate-value-container">
                <span className="solve-rate-percentage">{populationStats?.win || 0}%</span>
                <span className="solve-rate-subtext">
                  {populationStats?.win > 70 ? 'A Blockbuster' : populationStats?.win > 40 ? 'A Cult Classic' : 'An Arthouse film'} compared with recent puzzles
                </span>
              </div>
            </div>

            <div className="mistakes-breakdown-nyt">
              <h3>NUMBER OF MISTAKES TODAY</h3>
              <div className="nyt-dist-grid">
                <div className={`nyt-dist-item ${userCategory === 'win' ? 'active' : ''}`}>
                  <span className="nyt-dist-label">None</span>
                  <div className="nyt-dist-bar-container">
                    <div className="nyt-dist-bar" style={{ width: `${populationStats?.win || 0}%` }}></div>
                  </div>
                  <span className="nyt-dist-value">{populationStats?.win || 0}% of players {userCategory === 'win' && <span className="nyt-you-label">YOU</span>}</span>
                </div>
                <div className={`nyt-dist-item ${userCategory === 'great_attempt' ? 'active' : ''}`}>
                  <span className="nyt-dist-label">One</span>
                  <div className="nyt-dist-bar-container">
                    <div className="nyt-dist-bar" style={{ width: `${populationStats?.great_attempt || 0}%` }}></div>
                  </div>
                  <span className="nyt-dist-value">{populationStats?.great_attempt || 0}% of players {userCategory === 'great_attempt' && <span className="nyt-you-label">YOU</span>}</span>
                </div>
                <div className={`nyt-dist-item ${userCategory === 'good_attempt' ? 'active' : ''}`}>
                  <span className="nyt-dist-label">Two</span>
                  <div className="nyt-dist-bar-container">
                    <div className="nyt-dist-bar" style={{ width: `${populationStats?.good_attempt || 0}%` }}></div>
                  </div>
                  <span className="nyt-dist-value">{populationStats?.good_attempt || 0}% of players {userCategory === 'good_attempt' && <span className="nyt-you-label">YOU</span>}</span>
                </div>
                <div className={`nyt-dist-item ${userCategory === 'loss' ? 'active' : ''}`}>
                  <span className="nyt-dist-label">Three+</span>
                  <div className="nyt-dist-bar-container">
                    <div className="nyt-dist-bar" style={{ width: `${populationStats?.loss || 0}%` }}></div>
                  </div>
                  <span className="nyt-dist-value">{populationStats?.loss || 0}% of players {userCategory === 'loss' && <span className="nyt-you-label">YOU</span>}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="countdown-nyt">
          <p>NEXT PUZZLE IN</p>
          <span className="countdown-timer-nyt">{countdown}</span>
        </div>

        {updateFeedback && (
          <div className="feedback-nyt">
            <p>Quiz Feedback:</p>
            {!feedback ? <div className="feedback-buttons">
              <button className="feedback-button" onClick={handleLike}>👍</button>
              <button className="feedback-button" onClick={handleDislike}>👎</button>
            </div> : " 🙏🥳🙏 "}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsModal;
